// ============================================
// KANBAN COLUMN i18n HELPERS
// ============================================
//
// t12: localised labels for Kanban columns and filter rules. When a
// column has a single eq filterRule on a known enum field (status,
// priority, assignee) the column header displays the translated enum
// value (e.g. status=TODO → "To do" / "À faire") instead of the user-
// supplied column.name. For multi-rule columns or unknown fields we
// fall back to the user-supplied name (so user-created "Blocked" or
// "Has bug report" labels stay in place).

import type { ColumnDef, CustomField } from '@/lib/schemas';

// Plain translation function (the same shape as `t` from useI18n).
// We avoid importing useI18n here so this file stays a pure helper
// usable from non-React contexts. Accepting `any` key keeps the call
// sites ergonomic — the i18n dictionary keys we use here are all
// already declared in the Translations interface, so missing keys
// will fall back to the key string at runtime.
export type TranslateFn = (key: any) => string;

// Status and priority enums that we know how to translate. Mirrors the
// TaskStatus / TaskPriority enums in @/lib/schemas.
const STATUS_VALUES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED'] as const;
const PRIORITY_VALUES = ['NONE', 'LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;

// Returns a friendly localised label for the column:
// 1. Single eq rule on `status` → use kanban.statusValue.<value>
// 2. Single eq rule on `priority` → use kanban.priorityValue.<value>
// 3. Single eq rule on `assignee.id` → leave the user-supplied name
//    (assignee display is a person's name, no translation needed).
// 4. Otherwise → return column.name unchanged.
//
// The caller passes `t` from useI18n so the label is automatically in
// the active locale.
export function getColumnDisplayLabel(column: ColumnDef, t: TranslateFn): string {
    const rule = column.filterRules[0];
    if (!rule || column.filterRules.length !== 1 || rule.operator !== 'eq') {
        return column.name;
    }
    if (rule.field === 'status' && typeof rule.value === 'string' && STATUS_VALUES.includes(rule.value as any)) {
        return t(`kanban.statusValue.${rule.value}`);
    }
    if (rule.field === 'priority' && typeof rule.value === 'string' && PRIORITY_VALUES.includes(rule.value as any)) {
        return t(`kanban.priorityValue.${rule.value}`);
    }
    return column.name;
}

// Returns a friendly localised tag for the column's underlying filter
// field — used by the Columns popover so users don't see raw
// "CUSTOMFIELD.xxx" tokens (#4 in this round). Falls back to the raw
// field name if we can't resolve it (e.g. unknown custom field).
export function getColumnFieldTag(
    column: ColumnDef,
    customFields: CustomField[] | undefined,
    t: TranslateFn
): string {
    const rule = column.filterRules[0];
    if (!rule) return t('kanban.fieldTag.other');
    if (rule.field === 'status') return t('kanban.fieldTag.status');
    if (rule.field === 'priority') return t('kanban.fieldTag.priority');
    if (rule.field === 'assignee.id' || rule.field === 'assignee') return t('kanban.fieldTag.assignee');
    // Custom field — try to find the user-set name in the project's
    // customFields list. The rule's field is either `customField.<id>`
    // (t4+ form) or a bare custom-field id (legacy).
    if (customFields && customFields.length > 0) {
        const id = rule.field.startsWith('customField.') ? rule.field.slice('customField.'.length) : rule.field;
        const cf = customFields.find((c) => c.id === id);
        if (cf) return cf.name;
    }
    return t('kanban.fieldTag.customField');
}

// Translate a filter-rule operator into a short verb for the Add/Edit
// column dialog (#7). The default English "eq" / "neq" etc. is replaced
// with friendly verbs in the user's locale.
export function getOperatorLabel(operator: string, t: TranslateFn): string {
    const known = ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty', 'contains'];
    if (!known.includes(operator)) return operator;
    return t(`kanban.operator.${operator}`);
}

// Translate a field-id into a friendly label for the Add/Edit column
// dialog. Custom fields are looked up in the supplied list so users
// see their custom field names, not raw ids.
export function getFieldLabel(
    fieldId: string,
    customFields: CustomField[] | undefined,
    t: TranslateFn
): string {
    if (fieldId === 'status') return t('kanban.field.status');
    if (fieldId === 'priority') return t('kanban.field.priority');
    if (fieldId === 'assignee.id' || fieldId === 'assignee') return t('kanban.field.assignee');
    if (customFields && customFields.length > 0) {
        const id = fieldId.startsWith('customField.') ? fieldId.slice('customField.'.length) : fieldId;
        const cf = customFields.find((c) => c.id === id);
        if (cf) return cf.name;
    }
    return t('kanban.field.customField');
}

// Translate a status / priority enum value into its localised label.
// Used by the Add/Edit column dialog value dropdowns so users see
// "To do" / "À faire" instead of TODO.
export function getEnumValueLabel(field: string, value: string, t: TranslateFn): string {
    if (field === 'status' && STATUS_VALUES.includes(value as any)) {
        return t(`kanban.statusValue.${value}`);
    }
    if (field === 'priority' && PRIORITY_VALUES.includes(value as any)) {
        return t(`kanban.priorityValue.${value}`);
    }
    return value;
}
